import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Star, Shield, AlertTriangle, CheckCircle2, Hammer, Clock, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const formatFCFA = (n) => Number(n || 0).toLocaleString("fr-FR");

const STATUS_LABELS = {
  open: { label: "Ouvert aux propositions", color: "bg-[#D4AF37] text-[#0F172A]" },
  in_review: { label: "Acompte en attente", color: "bg-yellow-500 text-[#0F172A]" },
  in_progress: { label: "En cours", color: "bg-[#003594] text-white" },
  completed: { label: "Terminé", color: "bg-green-500 text-white" },
  disputed: { label: "Litige en cours", color: "bg-red-500 text-white" },
  cancelled: { label: "Annulé", color: "bg-gray-500 text-white" },
};

export default function ArtisanProjectDetail({ user }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProposalForm, setShowProposalForm] = useState(false);
  const [showDisputeForm, setShowDisputeForm] = useState(false);
  const [proposalData, setProposalData] = useState({ price: "", duration_days: "", message: "" });
  const [disputeData, setDisputeData] = useState({ reason: "", description: "" });

  const fetchProject = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/artisans/projects/${projectId}`);
      setProject(r.data);
    } catch {
      toast.error("Projet introuvable");
      navigate("/artisans");
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  if (loading || !project) {
    return (
      <div className="min-h-screen bg-[#0F172A] text-white flex items-center justify-center">
        <p className="text-white/60">Chargement du projet…</p>
      </div>
    );
  }

  const isClient = user?.id === project.client_id;
  const isArtisan = user?.id === project.selected_artisan_id;
  const myProposal = (project.proposals || []).find((p) => p.artisan_id === user?.id);
  const isOpen = project.status === "open";
  const escrowHeld = project.escrow_status === "held";
  const isInProgress = project.status === "in_progress";

  const submitProposal = async () => {
    if (!user?.id) {
      toast.error("Connectez-vous pour proposer");
      return;
    }
    const price = parseInt(proposalData.price);
    const days = parseInt(proposalData.duration_days);
    if (!price || price <= 0 || !days || days <= 0) {
      toast.error("Prix et durée valides requis");
      return;
    }
    try {
      await axios.post(`${API}/artisans/projects/${projectId}/proposals`, {
        artisan_id: user.id,
        price,
        duration_days: days,
        message: proposalData.message,
      });
      toast.success("Proposition envoyée !");
      setShowProposalForm(false);
      setProposalData({ price: "", duration_days: "", message: "" });
      fetchProject();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'envoi");
    }
  };

  const selectProposal = async (proposalId) => {
    if (!window.confirm("Sécuriser le paiement et lancer le projet avec cet artisan ?")) return;
    try {
      const r = await axios.post(`${API}/artisans/projects/${projectId}/select/${proposalId}`);
      if (r.data.wave_payment_url) {
        toast.success("Redirection vers Wave pour l'acompte…");
        window.open(r.data.wave_payment_url, "_blank");
      }
      fetchProject();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const releaseEscrow = async () => {
    if (!window.confirm("Confirmez-vous que la prestation est terminée et libérer le paiement à l'artisan ?")) return;
    try {
      await axios.post(`${API}/artisans/projects/${projectId}/release`);
      toast.success("Paiement libéré à l'artisan ✓");
      fetchProject();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const openDispute = async () => {
    if (!disputeData.reason) {
      toast.error("Indiquez la raison du litige");
      return;
    }
    try {
      await axios.post(`${API}/artisans/projects/${projectId}/dispute`, {
        project_id: projectId,
        raised_by: user.id,
        reason: disputeData.reason,
        description: disputeData.description,
        evidence_urls: [],
      });
      toast.success("Litige ouvert. L'équipe ARTEKNOS examinera sous 48h.");
      setShowDisputeForm(false);
      fetchProject();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const status = STATUS_LABELS[project.status] || { label: project.status, color: "bg-gray-500" };

  return (
    <div className="min-h-screen bg-[#0F172A] text-white pb-12" data-testid="project-detail-page">
      <div className="bg-gradient-to-br from-[#003380] via-[#003594] to-[#D4AF37] px-4 md:px-8 pt-6 pb-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <Button onClick={() => navigate("/artisans")} variant="ghost" size="sm" className="text-white hover:bg-white/20 rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <span className={`text-xs font-bold px-3 py-1 rounded-full ${status.color}`} data-testid="project-status">
              {status.label}
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-black" data-testid="project-title">{project.title}</h1>
          <p className="text-white/80 mt-2 text-sm">
            Budget : <strong>{formatFCFA(project.budget_min)} – {formatFCFA(project.budget_max)} FCFA</strong>
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-8 mt-6 space-y-4">
        {/* Project info */}
        <Card className="bg-[#1A1A1A] border-white/10">
          <CardContent className="py-5">
            <p className="text-white/80 whitespace-pre-line mb-4">{project.description}</p>
            <div className="flex flex-wrap gap-4 text-sm text-white/60">
              <span className="flex items-center gap-1.5"><Hammer className="w-4 h-4" /> {project.category}</span>
              <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {project.address}</span>
              {project.deadline && (
                <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> Avant {new Date(project.deadline).toLocaleDateString("fr-FR")}</span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Escrow status */}
        {escrowHeld && (
          <Card className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 border-green-500/40 border" data-testid="escrow-held-card">
            <CardContent className="py-4 flex items-center gap-3">
              <Shield className="w-7 h-7 text-green-400" />
              <div className="flex-1">
                <p className="font-bold text-green-300">Paiement sécurisé en escrow</p>
                <p className="text-sm text-white/70">
                  {formatFCFA(project.escrow_amount)} FCFA bloqués. Libérez les fonds après réception de la prestation.
                </p>
              </div>
              {isClient && (
                <Button onClick={releaseEscrow} className="bg-green-500 hover:bg-green-600 text-white font-bold" data-testid="release-escrow-btn">
                  <CheckCircle2 className="w-4 h-4 mr-1.5" />
                  Libérer
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Dispute CTA */}
        {(escrowHeld || isInProgress) && (isClient || isArtisan) && (
          <Card className="bg-[#1A1A1A] border-white/10">
            <CardContent className="py-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-white/70 text-sm">
                <AlertTriangle className="w-4 h-4 text-yellow-400" />
                Un problème ? Ouvrez un litige et notre équipe arbitre.
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowDisputeForm((s) => !s)}
                className="bg-white/5 border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/10"
                data-testid="open-dispute-btn"
              >
                Ouvrir un litige
              </Button>
            </CardContent>
          </Card>
        )}

        {showDisputeForm && (
          <Card className="bg-yellow-900/10 border-yellow-500/30 border">
            <CardContent className="py-5 space-y-3">
              <div>
                <Label className="text-white/80">Raison</Label>
                <Input
                  value={disputeData.reason}
                  onChange={(e) => setDisputeData((d) => ({ ...d, reason: e.target.value }))}
                  placeholder="Ex: Travail non conforme, retard, abandon de chantier…"
                  className="bg-transparent border-white/20 text-white"
                  data-testid="dispute-reason-input"
                />
              </div>
              <div>
                <Label className="text-white/80">Détails</Label>
                <Textarea
                  value={disputeData.description}
                  onChange={(e) => setDisputeData((d) => ({ ...d, description: e.target.value }))}
                  rows={3}
                  className="bg-transparent border-white/20 text-white"
                  placeholder="Décrivez précisément le problème"
                  data-testid="dispute-description-input"
                />
              </div>
              <Button onClick={openDispute} className="bg-yellow-500 text-[#0F172A] hover:bg-yellow-400 font-bold" data-testid="submit-dispute-btn">
                Soumettre le litige
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Proposals */}
        <div>
          <h2 className="text-xl font-bold text-white mb-3">
            Propositions reçues ({(project.proposals || []).length})
          </h2>

          {/* Submit proposal CTA (artisan side) */}
          {isOpen && !isClient && !myProposal && (
            <Card className="bg-[#1A1A1A] border-[#D4AF37]/40 border mb-4">
              <CardContent className="py-4">
                {!showProposalForm ? (
                  <Button onClick={() => setShowProposalForm(true)} className="w-full bg-gradient-to-r from-[#003594] to-[#D4AF37] text-white font-bold" data-testid="show-proposal-form-btn">
                    <Send className="w-4 h-4 mr-1.5" />
                    Envoyer ma proposition
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-white/80">Prix (FCFA)</Label>
                        <Input
                          type="number"
                          value={proposalData.price}
                          onChange={(e) => setProposalData((d) => ({ ...d, price: e.target.value }))}
                          className="bg-transparent border-white/20 text-white"
                          data-testid="proposal-price-input"
                        />
                      </div>
                      <div>
                        <Label className="text-white/80">Durée (jours)</Label>
                        <Input
                          type="number"
                          value={proposalData.duration_days}
                          onChange={(e) => setProposalData((d) => ({ ...d, duration_days: e.target.value }))}
                          className="bg-transparent border-white/20 text-white"
                          data-testid="proposal-days-input"
                        />
                      </div>
                    </div>
                    <Textarea
                      value={proposalData.message}
                      onChange={(e) => setProposalData((d) => ({ ...d, message: e.target.value }))}
                      placeholder="Présentez votre approche, vos références…"
                      className="bg-transparent border-white/20 text-white"
                      rows={3}
                      data-testid="proposal-message-input"
                    />
                    <div className="flex gap-2">
                      <Button onClick={() => setShowProposalForm(false)} variant="outline" className="bg-white/5 border-white/20 text-white">
                        Annuler
                      </Button>
                      <Button onClick={submitProposal} className="flex-1 bg-[#D4AF37] text-[#0F172A] font-bold" data-testid="submit-proposal-btn">
                        Envoyer
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {(project.proposals || []).length === 0 && (
            <Card className="bg-[#1A1A1A] border-white/10">
              <CardContent className="py-8 text-center">
                <p className="text-white/60">Aucune proposition pour le moment.</p>
              </CardContent>
            </Card>
          )}

          <div className="space-y-3">
            {(project.proposals || []).map((p) => (
              <Card
                key={p.id}
                className={`bg-[#1A1A1A] border ${
                  p.id === project.selected_proposal_id ? "border-[#D4AF37] border-2" : "border-white/10"
                }`}
                data-testid={`proposal-card-${p.id}`}
              >
                <CardContent className="py-4">
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#003594] to-[#D4AF37] flex items-center justify-center font-bold text-white">
                        {(p.artisan_name || "A").charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-bold text-white">{p.artisan_name || "Artisan"}</p>
                        <div className="flex items-center gap-1 text-xs text-white/60">
                          <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                          {(p.artisan_rating || 5.0).toFixed(1)} · {p.artisan_completed || 0} projets
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-black text-[#D4AF37]">{formatFCFA(p.price)} FCFA</p>
                      <p className="text-xs text-white/60">{p.duration_days} jours</p>
                    </div>
                  </div>
                  {p.message && <p className="text-sm text-white/80 mt-2">{p.message}</p>}
                  {isClient && isOpen && (
                    <Button
                      onClick={() => selectProposal(p.id)}
                      className="w-full mt-3 bg-gradient-to-r from-[#003594] to-[#D4AF37] text-white font-bold"
                      data-testid={`select-proposal-${p.id}`}
                    >
                      <Shield className="w-4 h-4 mr-1.5" />
                      Choisir & sécuriser le paiement
                    </Button>
                  )}
                  {p.id === project.selected_proposal_id && (
                    <div className="mt-3 text-xs text-[#D4AF37] font-semibold">✓ Proposition sélectionnée</div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
